import api from './api';

/**
 * Client helpers for the provider-accepted -> payment-required handoff
 * (frontend booking lifecycle task). The backend stays authoritative:
 * these helpers only read fresh server data and never infer state locally.
 *
 * How the customer app learns a provider accepted:
 *   GET /service-requests/{requestId} -> request.status === 'assigned'
 *   (the request document has NO booking_id field; the booking must be
 *   resolved from the consumer bookings list via service_request_id)
 *   GET /consumers/me/bookings        -> bookings[].service_request_id === requestId
 *   GET /bookings/{bookingId}         -> authoritative booking state
 */

export function extractBookingId(request) {
  return request?.booking_id || null;
}

/** Provider accept response -> booking id (for direct provider navigation). */
export function extractAcceptedBookingId(acceptResponse) {
  return acceptResponse?.booking?.id || acceptResponse?.booking_id || null;
}

/**
 * Payment is still required only when the backend says so.
 * - status payment_pending -> not yet paid
 * - booking.payment.status === 'paid' or payment.paid_at present -> done
 * Unknown/odd payloads are treated conservatively: a confirmed,
 * in_progress or completed booking never routes back to payment.
 */
export function paymentStillRequired(booking) {
  if (!booking) return false;
  const status = booking.status;
  if (!status || status === 'confirmed' || status === 'in_progress' || status === 'completed') {
    return false;
  }
  if (booking.payment?.status === 'paid' || booking.payment?.paid_at) return false;
  return status === 'payment_pending';
}

/** Only navigate on fresh, authoritative data — stale polls never redirect. */
export function shouldNavigateToPayment(request, booking) {
  if (!request || !booking) return false;
  if (request.status !== 'assigned' && request.status !== 'accepted') return false;
  if (extractBookingId(request) && extractBookingId(request) !== booking.id) return false;
  return paymentStillRequired(booking);
}

export async function fetchServiceRequest(requestId) {
  const { data } = await api.get(`/service-requests/${requestId}`);
  return data?.request || null;
}

export async function fetchBooking(bookingId) {
  const { data } = await api.get(`/bookings/${bookingId}`);
  return data?.booking || null;
}

/** Pure selector: pick the booking created for this service request. */
export function findBookingForRequest(bookings, requestId) {
  if (!Array.isArray(bookings) || !requestId) return null;
  return bookings.find((b) => b?.service_request_id === requestId) || null;
}

/**
 * Resolve the booking created for an accepted service request.
 * The customer request payload does not carry booking_id, so the accepted
 * booking is looked up from the consumer bookings list by service_request_id.
 * Returns the booking id or null (not found / lookup failed).
 */
export async function resolveBookingForRequest(requestId) {
  if (!requestId) return null;
  try {
    const { data } = await api.get('/consumers/me/bookings', {
      params: { page: 1, page_size: 20 },
    });
    const booking = findBookingForRequest(data?.bookings, requestId);
    return booking?.id || null;
  } catch {
    return null;
  }
}

export const TERMINAL_REQUEST_STATUSES = new Set([
  'cancelled',
  'expired',
  'failed_no_providers',
  'completed',
]);

// Auto-redirect guard: attempt the accepted -> payment hop once per request
// per browser/app session. The storage is injectable so tests can exercise
// the dedupe logic without touching real sessionStorage.
export const AUTO_REDIRECT_STORAGE_KEY = 'resqly:autoPaymentRedirect';

export function markAutoRedirected(requestId, storage) {
  let store = storage;
  if (store === undefined) {
    try {
      store = window.sessionStorage;
    } catch {
      return; // storage unavailable — auto-redirect simply retriggers, still safe
    }
  }
  if (!store) return;
  try {
    store.setItem(AUTO_REDIRECT_STORAGE_KEY, requestId);
  } catch {
    /* ignore quota/security errors */
  }
}

export function hasAutoRedirected(requestId, storage) {
  let store = storage;
  if (store === undefined) {
    try {
      store = window.sessionStorage;
    } catch {
      return false;
    }
  }
  if (!store) return false;
  try {
    return store.getItem(AUTO_REDIRECT_STORAGE_KEY) === requestId;
  } catch {
    return false;
  }
}
